import { BasicModalProps } from "../../../../pages/master/interfaces/BasicModalProps";
import { Modal } from "react-bootstrap";
import SecondaryButton from "../../../../pages/master/atom/buttons/SecondaryButton";
import { CartesianGrid, Legend, Line, LineChart, XAxis, YAxis, Tooltip, ReferenceLine } from "recharts";
import { useEffect, useMemo, useState } from "react";
import client from "../../../../axios";

interface GraphModalProps extends BasicModalProps {
  data: any[]; // 메인(가열) 데이터
  selectedRow: Record<string, unknown> | null;
}

export default function GraphModal(props: GraphModalProps) {
  // [1] 데이터 상태
  const [combinedData, setCombinedData] = useState<any[]>([]);
  
  // [2] 설정값 상태 (가열)
  const [minTemp1, setMinTemp1] = useState<number | null>(null);
  const [maxTemp1, setMaxTemp1] = useState<number | null>(null);
  
  // [3] 설정값 상태 (심부)
  const [minTemp2, setMinTemp2] = useState<number | null>(null);
  const [maxTemp2, setMaxTemp2] = useState<number | null>(null);

  // 시간(HH:mm:ss) -> 초(Number) 변환
  const timeToSeconds = (timeStr?: string) => {
    if (!timeStr) return 0;
    const [h, m, s] = timeStr.split(":").map(Number);
    return h * 3600 + m * 60 + s;
  };

  useEffect(() => {
    if (props.show && props.selectedRow) {
      const fetchData = async () => {
        try {
          // 1. 가열(메인) 데이터 가공
          const heatingData = props.data.map(row => ({
            timeSec: timeToSeconds(row.record_time),
            displayTime: row.record_time,
            heatingTemp: parseFloat(row.temperature),
            coreTemp: null // 심부 값은 null
          }));

          // 가열 설정값 로드
          const conf1 = await client.get('/api/temperature/minmaxtemp', { params: { id: props.selectedRow!.id } });
          setMinTemp1(conf1.data.minTemp);
          setMaxTemp1(conf1.data.maxTemp);

          let finalData = [...heatingData];

          // 2. 심부(연결) 데이터 가공
          if (props.selectedRow!.linkedId) {
            const resDetail = await client.get('/api/temperature/detail', { params: { id: props.selectedRow!.linkedId } });
            const resConf = await client.get('/api/temperature/minmaxtemp', { params: { id: props.selectedRow!.linkedId } });

            setMinTemp2(resConf.data.minTemp);
            setMaxTemp2(resConf.data.maxTemp);

            const coreData = resDetail.data.map((row: any) => ({
              timeSec: timeToSeconds(row.record_time),
              displayTime: row.record_time,
              heatingTemp: null, // 가열 값은 null
              coreTemp: parseFloat(row.temperature)
            }));

            // 데이터 합치기 (시간순 정렬)
            finalData = [...finalData, ...coreData].sort((a, b) => a.timeSec - b.timeSec);
          } else {
            setMinTemp2(null);
            setMaxTemp2(null);
          }

          setCombinedData(finalData);

        } catch (e) {
          console.error(e);
        }
      };
      fetchData();
    }
  }, [props.show, props.selectedRow, props.data]);

  // Y축 범위 계산
  const yDomain = useMemo(() => {
    const vals = combinedData.flatMap(d => [d.heatingTemp, d.coreTemp]).filter(v => v !== null) as number[];
    if (vals.length === 0) return ['auto', 'auto'];
    
    // 설정값(min/max)도 범위에 포함
    const mins = [Math.min(...vals)];
    if (minTemp1) mins.push(minTemp1);
    if (minTemp2) mins.push(minTemp2);

    const maxs = [Math.max(...vals)];
    if (maxTemp1) maxs.push(maxTemp1);
    if (maxTemp2) maxs.push(maxTemp2);

    return [Math.min(...mins) - 5, Math.max(...maxs) + 5];
  }, [combinedData, minTemp1, maxTemp1, minTemp2, maxTemp2]);

  return (
    <Modal show={props.show} centered size="xl">
      <Modal.Body>
        <div className="d-flex justify-content-between px-3">
          <p className="fw-bold">날짜: {props.selectedRow?.date as string}</p>
          <div className="d-flex gap-3 small">
            <span style={{ color: '#8884d8', background: 'transparent' }}>● 가열 온도</span>
            <span style={{ color: '#ff7300', background: 'transparent' }}>● 심부 온도</span>
          </div>
        </div>
        
        <div className='d-flex justify-content-center align-items-center'>
          <LineChart
            data={combinedData}
            width={1000}
            height={600}
            margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
          >
            <CartesianGrid stroke="#eee" />
            
            {/* X축: 초 단위로 배치하되, 라벨은 시간 포맷으로 표시 */}
            <XAxis
              dataKey="timeSec"
              type='number'
              domain={['dataMin', 'dataMax']}
              tickFormatter={(sec) => {
                const h = Math.floor(sec / 3600);
                const m = Math.floor((sec % 3600) / 60);
                const s = sec % 60;
                return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
              }}
            />
            
            <YAxis domain={yDomain} />
            
            <Tooltip
              labelFormatter={(sec) => {
                // 툴팁 시간 포맷
                const h = Math.floor(sec / 3600);
                const m = Math.floor((sec % 3600) / 60);
                const s = sec % 60;
                return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
              }}
            />
            <Legend />

            {/* 1. 가열 온도 라인 (Blue) */}
            <Line 
              type="monotone" 
              dataKey="heatingTemp" 
              name="가열 온도"
              stroke="#8884d8" 
              strokeWidth={2} 
              dot={{ r: 3 }}
              connectNulls={false} // 끊어서 그리기
            />

            {/* 2. 심부 온도 라인 (Red/Orange) */}
            <Line 
              type="monotone" 
              dataKey="coreTemp" 
              name="심부 온도"
              stroke="#ff7300" 
              strokeWidth={2} 
              dot={{ r: 3 }}
              connectNulls={false} 
            />

            {/* 기준선 (Reference Line) - 가열 (선 위에 표시: position: 'top') */}
            {minTemp1 && (
              <ReferenceLine 
                y={minTemp1} 
                stroke="green" 
                strokeDasharray="3 3" 
                label={{ value: `가열 최소(${minTemp1})`, position: 'top', fill: 'green', fontSize: 14 }} 
              />
            )}
            {maxTemp1 && (
              <ReferenceLine 
                y={maxTemp1} 
                stroke="red" 
                strokeDasharray="3 3" 
                label={{ value: `가열 최대(${maxTemp1})`, position: 'top', fill: 'red', fontSize: 14 }} 
              />
            )}

            {/* 기준선 (Reference Line) - 심부 (선 아래에 표시: position: 'bottom') */}
            {minTemp2 && (
              <ReferenceLine 
                y={minTemp2} 
                stroke="darkgreen" 
                strokeDasharray="2 2" 
                label={{ value: `심부 최소(${minTemp2})`, position: 'bottom', fill: 'darkgreen', fontSize: 14 }} 
              />
            )}
            {maxTemp2 && (
              <ReferenceLine 
                y={maxTemp2} 
                stroke="darkred" 
                strokeDasharray="2 2" 
                label={{ value: `심부 최대(${maxTemp2})`, position: 'bottom', fill: 'darkred', fontSize: 14 }} 
              />
            )}

          </LineChart>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <SecondaryButton onClick={() => props.setShow(false)}>닫기</SecondaryButton>
      </Modal.Footer>
    </Modal>
  )
}